import localization from '../../../shared/strings';
import { formatProductAttributeTitle } from '../../helpers/productAttributes';

const { autoMappedFieldLabels, customAttributes, validation } = localization.PRODUCT_INFO;

function humanizeUnknownField(field) {
  const withSpaces = formatProductAttributeTitle(field)
    .replace(/([a-z])([A-Z])/g, '$1 $2');

  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

function resolveFieldLabel(field) {
  if (!field) return field;

  if (field === 'meta') {
    return customAttributes;
  }

  if (field.startsWith('meta.')) {
    return formatProductAttributeTitle(field.slice(5));
  }

  return autoMappedFieldLabels[field] || humanizeUnknownField(field);
}

function isJoiEnvelope(message) {
  return message.includes('child "') && message.includes(' fails because ');
}

/**
 * Peel nested Joi `child "x" fails because [...]` envelopes down to a leaf rule.
 * @param {string} message
 * @returns {string}
 */
export function unwrapJoiLeaf(message) {
  let current = message.trim();

  while (isJoiEnvelope(current)) {
    const childMatch = current.match(/child "[^"]+" fails because \[(.+)\]$/);
    if (!childMatch) break;
    current = childMatch[1].trim();
  }

  const quotedLeafMatch = current.match(/^\["([^"]+)" (.+)\]$/);
  if (quotedLeafMatch) {
    return `"${quotedLeafMatch[1]}" ${quotedLeafMatch[2]}`;
  }

  return current;
}

function extractJoiField(message) {
  const childFields = [...message.matchAll(/child "([^"]+)" fails because/g)];
  if (childFields.length) {
    const field = childFields[childFields.length - 1][1];
    if (field !== 'body') return field;
  }

  const quotedField = message.match(/"([^"]+)"/);
  return quotedField?.[1] || null;
}

const RULES = [
  {
    re: /"([^"]+)" is not allowed to be empty/,
    format: ([, field]) => (
      field === 'title'
        ? validation.productNameRequired
        : validation.fieldRequiredLabel(resolveFieldLabel(field))
    ),
  },
  {
    re: /"([^"]+)" is required/,
    format: ([, field]) => (
      field === 'body'
        ? validation.updateFailed
        : validation.fieldRequiredLabel(resolveFieldLabel(field))
    ),
  },
  {
    re: /"([^"]+)" length must be less than or equal to (\d+) characters long/,
    format: ([, field, max]) => validation.fieldMaxLengthLabel(
      resolveFieldLabel(field),
      Number(max),
    ),
  },
  {
    re: /"([^"]+)" length must be at least (\d+) characters long/,
    format: ([, field, min]) => validation.fieldMinLengthLabel(
      resolveFieldLabel(field),
      Number(min),
    ),
  },
  {
    re: /"([^"]+)" must be a number/,
    format: ([, field]) => validation.fieldInvalidNumberLabel(resolveFieldLabel(field)),
  },
  {
    re: /"([^"]+)" must be a boolean/,
    format: ([, field]) => validation.fieldInvalidBooleanLabel(resolveFieldLabel(field)),
  },
  {
    re: /"([^"]+)" must be one of/,
    format: ([, field]) => validation.fieldInvalidOptionLabel(resolveFieldLabel(field)),
  },
  {
    re: /"([^"]+)" must be of type object/,
    format: ([, field]) => validation.fieldInvalidValueLabel(resolveFieldLabel(field)),
  },
];

function formatKnownRule(message) {
  for (const { re, format } of RULES) {
    const match = message.match(re);
    if (match) return format(match);
  }

  return null;
}

/**
 * Maps raw Joi / API validation messages from product update endpoints
 * to user-facing copy. Unrecognized Joi envelopes fall back to field-aware
 * or generic messages instead of leaking raw schema text.
 *
 * @param {string|null|undefined} rawMessage
 * @returns {string}
 */
export function formatProductUpdateErrorMessage(rawMessage) {
  if (!rawMessage || typeof rawMessage !== 'string') {
    return validation.updateFailed;
  }

  const unwrapped = unwrapJoiLeaf(rawMessage);
  const candidates = unwrapped === rawMessage
    ? [rawMessage]
    : [unwrapped, rawMessage];

  for (const candidate of candidates) {
    const formatted = formatKnownRule(candidate);
    if (formatted) return formatted;
  }

  const field = extractJoiField(unwrapped) || extractJoiField(rawMessage);
  if (field && field !== 'body') {
    return validation.fieldInvalidValueLabel(resolveFieldLabel(field));
  }

  if (isJoiEnvelope(rawMessage)) {
    return validation.updateFailed;
  }

  return rawMessage;
}
